import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  UserPreferences,
} from '../../api/user';
import { AppState } from "../../store";
import { pushEvent } from '../../Services/AnalyticsService';
import Dashboard from './Dashboard';
import { Spinner } from '@danfoss/webex-ui';
import DashboardItemPopup from './DashboardItemPopup';
import parse from 'html-react-parser';
import { DashBoardItem, OtherAppsProps } from './Types';
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend'
import { isMobileDevice } from '../../utils';
import { DashboardItems } from '../../store/dashboadItems/types';
import {
  updateUserPreferences as updateUserPreferencesAction,
  setUsedApplications,
  setRecommendedApplications,
  setRelevantWebsitesPreferences
} from '../../store/dashboadItems/actions'
import { useAuthentication } from '../../Providers/AuthenticationProvider';

const setRecommendedDisplayPreferences = (item: DashBoardItem, dashboardItems: DashboardItems, functions: any) => {
  // Update the hidden state in the temporary state
  const updatedApps = dashboardItems.recommended_applications?.map(app =>
    app.client_id === item.client_id
      ? { ...app, hidden: !item.hidden }
      : app
  );

  functions.setRecommendedApplications(updatedApps);
}

const setRelevantDisplayPreferences = (item: DashBoardItem, dashboardItems: DashboardItems, functions: any) => {
  // Update the hidden state in the temporary state
  const updatedWebsitesPreferences = dashboardItems.relevant_websites_preferences?.map(website =>
    website.id === item.id
      ? { ...website, hidden: !item.hidden }
      : website
  );

  functions.setRelevantWebsitesPreferences(updatedWebsitesPreferences);
}

const setUsedDisplayPreferences = (item: DashBoardItem, dashboardItems: DashboardItems, functions: any) => {
  // Update the hidden state in the temporary state
  const updatedApps = dashboardItems.used_applications?.map(app =>
    app.client_id === item.client_id
      ? { ...app, hidden: !item.hidden }
      : app
  );

  functions.setUsedApplications(updatedApps);
}

const setDisplayPreferences = (item: DashBoardItem, itemType: string, dashboardItems: DashboardItems, functions: any) => {
  switch (itemType) {
    case "recommended":
      setRecommendedDisplayPreferences(item, dashboardItems, functions);
      break;
    case "relevant":
      setRelevantDisplayPreferences(item, dashboardItems, functions);
      break;
    default:
      setUsedDisplayPreferences(item, dashboardItems, functions);
      break;
  }
}

const setUsedMovePreferences = (draggedItem: DashBoardItem, targetItem: DashBoardItem, dashboardItems: DashboardItems, functions: any) => {
  const usedApps = dashboardItems.used_applications || [];

  // Find the indices in the actual array
  const sourceIndex = usedApps.findIndex(app => app.client_id === draggedItem.client_id);
  const targetIndex = usedApps.findIndex(app => app.client_id === targetItem.client_id);

  if (sourceIndex === -1 || targetIndex === -1) return;

  // Create a new array with the item moved
  const reorderedApps = [...usedApps];
  const [movedItem] = reorderedApps.splice(sourceIndex, 1);
  reorderedApps.splice(targetIndex, 0, movedItem);

  functions.setUsedApplications(reorderedApps);
}

const setRelevantMovePreferences = (draggedItem: DashBoardItem, targetItem: DashBoardItem, dashboardItems: DashboardItems, functions: any) => {
  const relevantWebsites = dashboardItems.relevant_websites || [];

  // For relevant websites, the dragged item uses client_id which is actually the website's id
  const draggedId = draggedItem.client_id || draggedItem.id;
  const targetId = targetItem.client_id || targetItem.id;

  // Find the indices in the relevant_websites array
  const sourceIndex = relevantWebsites.findIndex(website => website.id === draggedId);
  const targetIndex = relevantWebsites.findIndex(website => website.id === targetId);

  if (sourceIndex === -1 || targetIndex === -1) return;

  // Create a new array with the item moved
  const reorderedWebsites = [...relevantWebsites];
  const [movedWebsite] = reorderedWebsites.splice(sourceIndex, 1);
  reorderedWebsites.splice(targetIndex, 0, movedWebsite);

  // Update preferences based on new order
  const updatedPrefs = reorderedWebsites.map((website, index) => ({
    id: website.id || '',
    hidden: website.hidden || false,
    display_order: index
  }));

  functions.setRelevantWebsitesPreferences(updatedPrefs);
}

const setMovePreferences = (draggedItem: DashBoardItem, targetItem: DashBoardItem, itemType: string, dashboardItems: DashboardItems, functions: any) => {
  switch (itemType) {
    case "used":
      setUsedMovePreferences(draggedItem, targetItem, dashboardItems, functions);
      break;
    case "relevant":
      setRelevantMovePreferences(draggedItem, targetItem, dashboardItems, functions);
      break;
  }
}

const setDashBoardItemAfterReload = (dashboardItem: DashBoardItem, dashboardItems: DashboardItems, setDashboardItem: Function, setShowDashboardItemDescription: Function) => {
  if (dashboardItem.client_id) {

    const dashItem =
      dashboardItems.used_applications?.find(item => item.client_id === dashboardItem.client_id) ??
      dashboardItems.authorized_applications?.find(item => item.client_id === dashboardItem.client_id) ??
      dashboardItems.recommended_applications?.find(item => item.client_id === dashboardItem.client_id);

    if (dashItem?.translated_description) {
      setDashboardItem(dashItem);
    } else {
      setDashboardItem({} as DashBoardItem);
      setShowDashboardItemDescription(false);
    }
  } else if (dashboardItem.id) {

    const dashItem = dashboardItems.relevant_websites?.find(item => item.id === dashboardItem.id)

    if (dashItem?.description) {
      setDashboardItem(dashItem)
    } else {
      setDashboardItem({} as DashBoardItem)
      setShowDashboardItemDescription(false);
    }
  }
}

const DashboardWrapper: React.FC<OtherAppsProps> = ({ profile, dashboardItems, updateUserPreferences, setUsedApplications, setRecommendedApplications, setRelevantWebsitesPreferences, setLeaveConfirmValues }) => {

  const [showDashboardItemDescription, setShowDashboardItemDescription] = useState(false);
  const [dashboardItem, setDashboardItem] = useState({} as DashBoardItem);
  const [editMode, setEditMode] = React.useState(false);
  const { user } = useAuthentication();
  const backendFactory = isMobileDevice() ? TouchBackend : HTML5Backend;

  useEffect(() => {
    setDashBoardItemAfterReload(dashboardItem, dashboardItems, setDashboardItem, setShowDashboardItemDescription);
  }, [dashboardItems, dashboardItems.relevant_websites_loaded, dashboardItems.applications_loaded]);

  useEffect(() => {
    // Push an event that a user entered the applications page
    pushEvent(
      {
        'event': 'virtualPageview',
        'page': `${(window as any).location.hostname}/other-apps`
      },
      user
    );
  }, []);



  const handleInformationIconClick = (item: DashBoardItem) => {
    setDashboardItem(item)
    setShowDashboardItemDescription(!showDashboardItemDescription);
  }

  const toggleApplicationDescription = () => {
    setShowDashboardItemDescription(!showDashboardItemDescription);
  }

  const setLeaveValues = (obj1: any, obj2: any, resetFunction: Function) => {
    setLeaveConfirmValues && setLeaveConfirmValues(obj1, obj2, true, resetFunction, document.getElementById('saveButton'))
  }

  const handleDashboardItemDisplay = (item: DashBoardItem, itemType: string) => {

    // we need to tell the clickoutsidewrapper that a change have happened. No need to send
    // actual values of the changed events, just send two string that doesn't match
    setLeaveValues("1", "2", () => { handleResetUserPreferences(); setEditMode(false) });

    setDisplayPreferences(item, itemType, dashboardItems, {
      setUsedApplications,
      setRecommendedApplications,
      setRelevantWebsitesPreferences
    });
  }

  const handleMoveDashboardItem = (draggedItem: any, targetItem: any, itemType: string) => {

    // we need to tell the clickoutsidewrapper that a change have happened. No need to send
    // actual values of the changed events, just send two string that doesn't match
    setLeaveValues("1", "2", () => { handleResetUserPreferences(); setEditMode(false) });
    setMovePreferences(draggedItem, targetItem, itemType, dashboardItems, {
      setUsedApplications,
      setRecommendedApplications,
      setRelevantWebsitesPreferences
    });
  }

  const handleUpdateUserPreferences = () => {
    // Build preferences with display_order as array index (0, 1, 2...)
    const usedAppsPreferences = {
      preferences: dashboardItems.used_applications?.map((app, index) => ({
        client_id: app.client_id,
        hidden: app.hidden,
        display_order: index + 1
      })) || []
    };

    const recommendedAppsPreferences = {
      preferences: dashboardItems.recommended_applications?.map((app, index) => ({
        client_id: app.client_id,
        hidden: app.hidden,
        display_order: index + 1
      })) || []
    };

    const relevantWebsitesPreferences = {
      preferences: dashboardItems.relevant_websites_preferences,
    };

    updateUserPreferences({
      isMobile: isMobileDevice(),
      used_application_preferences: usedAppsPreferences,
      recommended_application_preferences: recommendedAppsPreferences,
      relevant_websites_preferences: relevantWebsitesPreferences
    });

    // we need to tell the clickoutsidewrapper that a change have happened. No need to send
    // actual values of the changed events, just send two string that doesn't match
    setLeaveValues("1", "1", () => { });
  }

  const handleResetUserPreferences = () => {
    // Reset will reload from backend, which will restore original state
    updateUserPreferences({
      isMobile: isMobileDevice(),
      used_application_preferences: { preferences: [] },
      recommended_application_preferences: { preferences: [] },
      relevant_websites_preferences: { preferences: [] }
    });

    // we need to tell the clickoutsidewrapper that a change have happened. No need to send
    // actual values of the changed events, just send two string that doesn't match
    setLeaveValues("1", "1", () => { });
  }

  if (showDashboardItemDescription) {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  return !dashboardItems ? (<Spinner visible={!dashboardItems} />) : (
    <>
      {showDashboardItemDescription && (
        <DashboardItemPopup
          headerText={dashboardItem.translated_name}
          bodyText={parse(dashboardItem.translated_description || '')}
          togglePopup={toggleApplicationDescription}
        />
      )}
      { /* @ts-ignore */}
      <DndProvider backend={backendFactory}>
        <Dashboard
          profile={profile}
          dashboardItems={dashboardItems}
          handleInformationIconClick={handleInformationIconClick}
          handleMoveDashboardItem={handleMoveDashboardItem}
          handleDashboardItemDisplay={handleDashboardItemDisplay}
          handleUpdateUserPreferences={handleUpdateUserPreferences}
          handleResetUserPreferences={handleResetUserPreferences}
          editMode={editMode}
          setEditMode={setEditMode}
        />
      </DndProvider>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => () => ({
  updateUserPreferences: (userPreferences: UserPreferences) => dispatch(updateUserPreferencesAction(userPreferences)),
  setUsedApplications: (apps: any) => dispatch(setUsedApplications(apps)),
  setRecommendedApplications: (apps: any) => dispatch(setRecommendedApplications(apps)),
  setRelevantWebsitesPreferences: (prefs: any) => dispatch(setRelevantWebsitesPreferences(prefs))
});

const mapStateToProps = (state: AppState) => ({
  profile: state.profile,
  cultures: state.cultures,
  dashboardItems: state.dashboardItems
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardWrapper);
